import { FC, useEffect, useRef, useState } from 'react';

import useMwdTimescale from '@/apps/mwd-v2/state/selectors/useMwdTimescale';
import ChevronLeft from '@/assets/icons/ChevronLeft';
import Close from '@/assets/icons/Close';
import DefectBeperktInzetbaarXs from '@/assets/icons/DefectBeperktInzetbaarXs';
import DefectRow from '@/components/defectRow/DefectRow';
import { MaterieelEigenschappen } from '@/components/detailPanel/materieelDetails/useMaterieelEigenschappen';
import NietInzetbaarPill from '@/components/NietInzetbaarPill/NietInzetbaarPill';
import { CollapsibleContent } from '@/components/UI/Collapsible/Collapsible';
import CollapsibleContainer from '@/components/UI/Collapsible/CollapsibleContainer';
import Pill from '@/components/UI/Pill/Pill';
import { getUniekeRijBeperkingDefecten } from '@/domain/defecten/rijBeperkingen';
import useAvailableHeight from '@/hooks/useAvailableHeight';
import { Defect } from '@/types/Defecten';
import { Eenheid } from '@/types/werklijnenInline';

import styles from '../DetailPanel.module.css';
import collapsibleStyles from '@/components/UI/Collapsible/Collapsible.module.css';

type PillKey = 'snelheid' | 'rij';

/** Moet gelijk lopen met de duur van de defectRowHighlightFlash animatie in DetailPanel.module.css. */
const HIGHLIGHT_DURATION_MS = 2000;

export type MaterieelDetailsPanelProps = {
  materieelPanelData?: Eenheid;
  eigenschappen?: MaterieelEigenschappen | null;
  defecten: Defect[];
  onClose: () => void;
  onBack?: () => void;
  onMaterieelTypeClick?: () => void;
};

const formatMaterieelTypeLabel = (materieelType?: Eenheid['materieelType']) => {
  if (!materieelType?.soort && !materieelType?.aanduiding) return '';
  if (!materieelType?.aanduiding) return materieelType.soort;
  if (!materieelType?.soort) return materieelType.aanduiding;
  return `${materieelType.soort}-${materieelType.aanduiding}`;
};

const MaterieelDetailsPanel: FC<MaterieelDetailsPanelProps> = ({
  materieelPanelData,
  eigenschappen,
  defecten,
  onClose,
  onBack,
  onMaterieelTypeClick,
}) => {
  const { end: horizonEinde } = useMwdTimescale();
  const [openEigenschappen, setOpenEigenschappen] = useState(true);
  const [openDefecten, setOpenDefecten] = useState(true);
  const [highlight, setHighlight] = useState<{ defectId: number; pill: PillKey; seq: number } | null>(null);
  const highlightTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const sectionsWrapperRef = useRef<HTMLDivElement>(null);
  const eigenschappenBlockRef = useRef<HTMLDivElement>(null);
  const defectenContainerRef = useRef<HTMLDivElement>(null);
  const heeftExtraEigenschappen = Boolean(
    eigenschappen?.aantalAssen ||
    eigenschappen?.maxSnelheid ||
    eigenschappen?.onderhoudLocatie ||
    eigenschappen?.volNorm ||
    eigenschappen?.lengteOverBuffers ||
    eigenschappen?.massaLeegKg,
  );

  // Elke pill wordt afgeleid van één specifiek defect, zodat klikken exact die werkorder markeert.
  // Rijbeperking (bijv. NOEK) komt van de werkorder, niet uit het materieelpaneel.
  const rijBeperkingDefecten = getUniekeRijBeperkingDefecten(defecten);
  const snelheidBeperkingDefect = defecten.find((defect) => defect.snelheidBeperking) ?? null;

  const defectenMaxHeight = useAvailableHeight(sectionsWrapperRef, defectenContainerRef, {
    minHeight: 120,
    observedRefs: [eigenschappenBlockRef],
    deps: [openEigenschappen, openDefecten, defecten.length],
  });

  const highlightedDefectId = highlight?.defectId ?? null;
  // De pill-status volgt de aangeklikte pill, niet het defect: één werkorder kan meerdere beperkingen hebben.
  const isPillActief = (pill: PillKey, defectId?: number) =>
    highlight?.pill === pill && highlight.defectId === defectId;
  // Een pill is alleen klikbaar als het bijbehorende defect ook echt in de defectenlijst staat.
  const isZichtbaar = (defect: Defect | null) => !!defect && defecten.some((zichtbaar) => zichtbaar.id === defect.id);

  useEffect(() => () => clearTimeout(highlightTimeout.current), []);

  // De highlight is bewust tijdelijk: hij licht kort op en verdwijnt daarna weer.
  const flashDefect = (defectId: number, pill: PillKey) => {
    setOpenDefecten(true);
    clearTimeout(highlightTimeout.current);
    setHighlight((current) => ({ defectId, pill, seq: (current?.seq ?? 0) + 1 }));
    highlightTimeout.current = setTimeout(() => setHighlight(null), HIGHLIGHT_DURATION_MS);
  };

  const isSnelheidActief = isPillActief('snelheid', snelheidBeperkingDefect?.id);
  const isSnelheidZichtbaar = isZichtbaar(snelheidBeperkingDefect);

  return (
    <div className={styles.detailPanelContainer}>
      <div className={styles.detailPanelHeader}>
        <div className={styles.detailPanelTitle}>
          <h3>
            {onBack && (
              <button
                type='button'
                className={`${styles.navButton} ${styles.navIcon} ${styles.navIconBack}`}
                onClick={onBack}
                data-testid='back-button-materieel-panel'
                aria-label='Ga terug'
              >
                <ChevronLeft width={16} height={16} />
              </button>
            )}
            Eenheid {materieelPanelData?.nummer ?? 'onbekend'}
            <span
              className={styles.materieelTypeLabel}
              onClick={onMaterieelTypeClick}
              data-testid={`${materieelPanelData?.nummer ?? 'onbekend'}-materieel-details`}
            >
              {formatMaterieelTypeLabel(materieelPanelData?.materieelType)}
            </span>
          </h3>
          <button
            type='button'
            data-testid='close-detailPanel'
            className={styles.closeButton}
            onClick={onClose}
            aria-label='Sluiten'
          >
            <Close aria-hidden='true' fill='var(--tractie-color-content-subtle)' width={24} height={24} />
          </button>
        </div>
        <div className={styles.detailPanelHeaderInfo}>
          <div className={styles.materieelPillsRow}>
            <NietInzetbaarPill eenheid={materieelPanelData} horizonEinde={horizonEinde} inverse={false} />
            {eigenschappen?.snelheidBeperking && (
              <Pill
                variant='warning'
                icon={DefectBeperktInzetbaarXs}
                testid='pill-snelheidbeperking'
                onClick={
                  isSnelheidZichtbaar && snelheidBeperkingDefect
                    ? () => flashDefect(snelheidBeperkingDefect.id, 'snelheid')
                    : undefined
                }
                selected={isSnelheidActief}
                className={isSnelheidActief ? styles.pillHighlightFlash : undefined}
                ariaLabel={
                  isSnelheidZichtbaar
                    ? `Snelheidsbeperking ${eigenschappen.snelheidBeperking}, toon het bijbehorende defect`
                    : undefined
                }
              >
                {`Max: ${eigenschappen.snelheidBeperking}`}
              </Pill>
            )}
            {rijBeperkingDefecten.map((rijBeperkingDefect) => {
              const isRijActief = isPillActief('rij', rijBeperkingDefect.id);
              const isRijZichtbaar = isZichtbaar(rijBeperkingDefect);

              return (
                <Pill
                  key={rijBeperkingDefect.id}
                  variant='warning'
                  icon={DefectBeperktInzetbaarXs}
                  testid='pill-rijbeperking'
                  onClick={isRijZichtbaar ? () => flashDefect(rijBeperkingDefect.id, 'rij') : undefined}
                  selected={isRijActief}
                  className={isRijActief ? styles.pillHighlightFlash : undefined}
                  ariaLabel={
                    isRijZichtbaar
                      ? `Rijbeperking ${rijBeperkingDefect.rijBeperking}, toon het bijbehorende defect`
                      : undefined
                  }
                >
                  {rijBeperkingDefect.rijBeperking}
                </Pill>
              );
            })}
          </div>
        </div>
      </div>

      <p aria-live='polite' className={styles.visuallyHidden} data-testid='beperking-highlight-status'>
        {highlightedDefectId ? 'Bijbehorend defect gemarkeerd' : ''}
      </p>

      <div className={styles.materieelSectionsWrapper} ref={sectionsWrapperRef}>
        <div className={styles.materieelSectionBlock} ref={eigenschappenBlockRef}>
          <div className={styles.eigenschappenContainer}>
            <ul className={styles.eigenschappenList}>
              {eigenschappen?.deelpark && eigenschappen.deelpark.length > 0 && (
                <li className={styles.listItemRow}>
                  <span>Deelpark</span>
                  <span>{eigenschappen.deelpark}</span>
                </li>
              )}
              {eigenschappen?.zitPlaatsen && (
                <li className={styles.listItemRow}>
                  <span>Zitplaatsen (2e)</span>
                  <span>{eigenschappen.zitPlaatsen}</span>
                </li>
              )}
              {eigenschappen?.capaciteitAcceptabel && (
                <li className={styles.listItemRow}>
                  <span>Norm acceptabel (2e)</span>
                  <span>{eigenschappen.capaciteitAcceptabel}</span>
                </li>
              )}
              {eigenschappen?.opmerkingen && (
                <li className={styles.listItemRow}>
                  <span>Opmerkingen</span>
                  <span>{eigenschappen.opmerkingen}</span>
                </li>
              )}
            </ul>

            {heeftExtraEigenschappen && (
              <>
                <CollapsibleContent
                  className={collapsibleStyles.CollapsibleContent}
                  open={openEigenschappen}
                  onOpenChange={setOpenEigenschappen}
                >
                  <ul className={styles.eigenschappenList} id='eigenschappen-meer'>
                    {eigenschappen?.aantalAssen && (
                      <li className={styles.listItemRow}>
                        <span>Aantal assen</span>
                        <span>{eigenschappen.aantalAssen}</span>
                      </li>
                    )}
                    {eigenschappen?.maxSnelheid && (
                      <li className={styles.listItemRow}>
                        <span>Max snelheid</span>
                        <span className={styles.speedColumn}>
                          <span
                            className={
                              eigenschappen.snelheidBeperking
                                ? `${styles.strikeThroughStyle} ${styles.speedError}`
                                : undefined
                            }
                          >
                            {`${eigenschappen.maxSnelheid} km/u`}
                          </span>
                          {eigenschappen.snelheidBeperking && <span>{`${eigenschappen.snelheidBeperking}`}</span>}
                        </span>
                      </li>
                    )}
                    {eigenschappen?.onderhoudLocatie && (
                      <li className={styles.listItemRow}>
                        <span>Onderhoudslocatie</span>
                        <span>{eigenschappen.onderhoudLocatie}</span>
                      </li>
                    )}
                    {eigenschappen?.volNorm && (
                      <li className={styles.listItemRow}>
                        <span>Volnorm</span>
                        <span>{eigenschappen.volNorm}</span>
                      </li>
                    )}
                    {eigenschappen?.lengteOverBuffers && (
                      <li className={styles.listItemRow}>
                        <span>Lengte over buffers</span>
                        <span>{`${eigenschappen.lengteOverBuffers} m`}</span>
                      </li>
                    )}
                    {eigenschappen?.massaLeegKg && (
                      <li className={styles.listItemRow}>
                        <span>Massa leeg</span>
                        <span>{`${eigenschappen.massaLeegKg} ton`}</span>
                      </li>
                    )}
                  </ul>
                </CollapsibleContent>

                <button
                  type='button'
                  className={styles.toonMeerButton}
                  data-testid='toggle-eigenschappen'
                  aria-expanded={openEigenschappen}
                  aria-controls='eigenschappen-meer'
                  onClick={() => setOpenEigenschappen((open) => !open)}
                >
                  {openEigenschappen ? 'Toon minder' : 'Toon meer'}
                </button>
              </>
            )}
          </div>
        </div>
        {defecten.length > 0 && (
          <div className={styles.materieelSectionBlock}>
            <CollapsibleContainer
              title={<span className={styles.materieelSectionTitle}>Defecten</span>}
              open={openDefecten}
              badge={defecten.length}
              onOpenChange={setOpenDefecten}
              showTopBorder={false}
              headerPadding='16px var(--tractie-spacing-inset-m, 16px)'
              titleLeftMargin={0}
            >
              <div
                className={styles.defectenContainer}
                ref={defectenContainerRef}
                style={defectenMaxHeight ? { maxHeight: `${defectenMaxHeight}px` } : undefined}
              >
                {defecten.map((defect, index) => {
                  const isMatch = highlightedDefectId === defect.id;
                  return (
                    <div
                      data-testid={`tooltip-${defect.toelichting}`}
                      className={`${styles.defectRowWrapper} ${isMatch ? styles.defectRowWrapperHighlighted : ''}`}
                      key={isMatch ? `${defect.id}-highlight-${highlight?.seq}` : defect.id}
                    >
                      <DefectRow
                        stackedLayout
                        noPadding
                        toonIconNietInzetbaar
                        showYearDate={false}
                        showEenheidNummer={false}
                        disabledHoverAndBorder
                        showBorderBottom={index < defecten.length - 1}
                        defect={defect}
                        positie='enkelvoudig'
                      />
                    </div>
                  );
                })}
              </div>
            </CollapsibleContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default MaterieelDetailsPanel;
